import { createPlaceholderKeys, withKeys } from "@/common/helpers/list-keys";
import { cn } from "@/common/helpers/utils";
import { EmptyState } from "@/components/data-table/_components/empty-state";
import { Button, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { getItemIdentifier, isItemSelected } from "../_helpers/item-identifier";
import { DataListContentProps, ItemWithIdentifier } from "../types";

export function DataListContent<TData extends ItemWithIdentifier = ItemWithIdentifier>({
  data,
  itemRenderer,
  isLoading,
  isError,
  activeFilterCount,
  searchTerm,
  containerClassName = "",
  itemClassName = "",
  itemLayout = "wrap",
  onItemClick,
  itemActions,
  onClearAllFilters,
  onClearSearch,
  emptyStateComponent,
  loadingComponent,
  errorComponent,
  selection,
  features,
}: DataListContentProps<TData>) {
  const { t } = useTranslation("common");
  const divId = useId();

  const gridClasses =
    itemLayout === "grid"
      ? "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      : "flex flex-wrap gap-3";

  const renderInner = () => {
    if (isLoading) {
      if (loadingComponent) {
        return <div className={containerClassName}>{loadingComponent}</div>;
      }

      return (
        <div className={cn("flex-1 overflow-auto p-4", containerClassName)}>
          <div className={cn(gridClasses, itemClassName)}>
            {createPlaceholderKeys(6, divId).map((key) => (
              <div
                key={key}
                className={cn(
                  "flex items-center gap-3 rounded-lg border p-3",
                  itemLayout === "grid" && "h-full w-full",
                )}
              >
                <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-32" />
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (isError) {
      if (errorComponent) {
        return <div className={containerClassName}>{errorComponent}</div>;
      }

      return (
        <div className={cn("flex flex-col items-center justify-center p-4 py-12", containerClassName)}>
          <AlertCircle className="text-muted-foreground mb-4 h-12 w-12" />
          <h3 className="mb-2 text-lg font-semibold">{t("ErrorLoading")}</h3>
          <p className="text-muted-foreground text-center">{t("ErrorLoadingDataMessage")}</p>
        </div>
      );
    }

    if (data.length === 0) {
      if (emptyStateComponent) {
        return <div className={containerClassName}>{emptyStateComponent}</div>;
      }

      return (
        <div className={containerClassName}>
          <EmptyState
            hasFilters={activeFilterCount > 0}
            hasSearch={!!searchTerm}
            searchTerm={searchTerm}
            onClearFilters={activeFilterCount > 0 ? onClearAllFilters : undefined}
            onClearSearch={searchTerm ? onClearSearch : undefined}
          />
        </div>
      );
    }

    return (
      <div className={cn("flex-1 overflow-auto p-4", containerClassName)}>
        <div className={cn(gridClasses, itemClassName)}>
          {data.map((item, index) => {
            const itemId = getItemIdentifier(item);
            const isSelected = isItemSelected(item, selection);
            const isClickable = !!onItemClick;

            return (
              <div
                key={itemId}
                role={isClickable ? "button" : undefined}
                tabIndex={isClickable ? 0 : undefined}
                className={cn(
                  "group bg-background/40 relative block rounded-xl border border-transparent text-left transition-all",
                  itemLayout === "grid" && "h-full w-full",
                  isClickable && "hover:border-border hover:bg-muted/20 cursor-pointer",
                )}
                onClick={() => {
                  if (isClickable) onItemClick(item);
                }}
                onKeyDown={(e) => {
                  if (!isClickable) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onItemClick(item);
                  }
                }}
              >
                {features.multiSelection && isSelected && (
                  <div className="bg-primary/10 border-primary/40 pointer-events-none absolute inset-0 rounded-xl border-2" />
                )}

                <div className="relative rounded-xl">{itemRenderer(item, index)}</div>

                {itemActions && itemActions.length > 0 && (
                  <div className="absolute top-2 right-2 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
                    <div className="flex gap-1">
                      {withKeys(
                        itemActions.filter((action) => !action.visible || action.visible(item)),
                        (action, actionIndex) => `action-${action.label}-${actionIndex}`,
                      ).map(({ key, item: action }) => (
                        <Button
                          key={key}
                          variant={action.variant ?? ButtonVariant.Ghost}
                          size={ButtonSize.Sm}
                          onClick={(e) => {
                            e.stopPropagation();
                            action.action(item);
                          }}
                          disabled={action.disabled?.(item)}
                          className="h-7 w-7 rounded-md p-0"
                          tooltip={action.label}
                        >
                          {action.icon}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className={cn("border-border bg-background relative h-full overflow-hidden rounded-lg border")}>
        {renderInner()}
      </div>
    </div>
  );
}
